import { makeAutoObservable, runInAction } from 'mobx'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CallApi, type IResponseDefaultAudio } from 'entities/Call'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type ISmartCallFlowGreetingAudioStepBlockArgs } from 'entities/SmartCall/api/types'
import { cacheStore } from 'entities/Cache/CacheStore.ts'
import { GreetingStore } from 'entities/Greetings/store/GreetingStore.tsx'
import { checkUsageGreetingVoice } from 'features/ItemUsage/actions/checkUsageGreetingVoice.tsx'
import { RinglessMessageFieldStore } from 'widgets/MessageFormFields/RinglessMessageField'
import { RenameModalStore } from 'widgets/RenameModal'
import { MediaLibraryVariant } from 'widgets/MediaLibrary/types/types'

export class GreetingAudioBlockStore {
  ringlessMessageFieldStore: RinglessMessageFieldStore
  errorMessage = 'Please select an audio'
  readonly greetingStore = new GreetingStore()
  constructor(
    private _entities: IEntitiesStore,
    readonly parent: { inbox: Inbox | null },
    args?: ISmartCallFlowGreetingAudioStepBlockArgs,
    defaultProps?: {
      isDefault: boolean
      defaultType?: IResponseDefaultAudio['category']
    }
  ) {
    this.ringlessMessageFieldStore = new RinglessMessageFieldStore(this._entities, {
      ttsGroup: 'voicemail',
      withAudioDurationValidation: true,
      withMinAudioDurationValidation: false,
      clearParentError: () => {
        this.setError('')
      },
      onAddAudio: async (file: File, onAddAttachment) => {
        try {
          const attachment = await this.greetingStore.uploadGreeting(file)
          onAddAttachment(attachment)
        } catch (e) {
          logger.error(e)
        }
      },
      checkCanRename: (attachment) => !attachment.responseDefaultAudio,
      onRenameAudio: (attachment) => this._handleRenameGreetingAudio(attachment),
      mediaLibraryConfiguration: {
        onRenameAudio: (attachment) => this._handleRenameGreetingAudio(attachment),
        mediaLibraryVariant: MediaLibraryVariant.AudioSelect,
        onDeleteAttachments: async (ids) => {
          const numericIds = ids.map(Number)
          await this.greetingStore.deleteGreetings(numericIds)
        },
        checkBeforeDeleteAttachments: async (ids) => {
          const numericIds = ids.map(Number)
          const isUsage = await checkUsageGreetingVoice(numericIds)
          return !isUsage
        },
        onAddFile: async (file: File) => this.greetingStore.uploadGreeting(file),
        fetchAttachmentList: async (term = '') => {
          await this.greetingStore.loadGreetings()
          return this.greetingStore.attachments.filter((item) =>
            item.name?.toLowerCase().includes(term.toLowerCase())
          )
        },
      },
    })
    if (defaultProps?.defaultType) {
      this.setDefaultCategory(defaultProps?.defaultType)
    }
    if (args?.audio_url) {
      this._initAudio(args.audio_url, !!defaultProps?.isDefault)
    } else if (defaultProps?.isDefault) {
      this.initDefaultAudio()
    }
    makeAutoObservable(this)
  }

  private _initAudioUrl: string | null = null
  private _initAudio = async (audio_url: string, withDefault: boolean) => {
    try {
      this._initAudioUrl = audio_url
      this.ringlessMessageFieldStore.setInitializing(true)

      const setFromGreetings = async () => {
        const attachment = this.greetingStore.attachments.find((item) => item.source === audio_url)
        if (attachment) {
          this.ringlessMessageFieldStore.onAddAttachmentAudio(attachment)
        }
      }
      if (withDefault) {
        const [audios] = await Promise.all([
          this._getDefaultAudios(),
          this.greetingStore.loadGreetings(),
        ])
        if (audios) {
          const audio = audios.find((item) => item.url === audio_url)
          if (audio) {
            this.setDefaultCategory(audio.category)
            this.ringlessMessageFieldStore.onAddAttachmentAudio(
              new Attachment({ responseDefaultAudio: audio })
            )
          } else {
            await setFromGreetings()
          }
        }
      } else {
        await this.greetingStore.loadGreetings()
        await setFromGreetings()
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._initAudioUrl = null
        this.ringlessMessageFieldStore.setInitializing(false)
      })
    }
  }
  get args(): ISmartCallFlowGreetingAudioStepBlockArgs {
    if (this._initAudioUrl) {
      return {
        audio_url: this._initAudioUrl,
      }
    }
    const attachment = this.ringlessMessageFieldStore.attachment
    return {
      audio_url: attachment?.source || '',
    }
  }

  validate = () => {
    const isValid = !!this.args?.audio_url
    if (!isValid) {
      this.setError(this.errorMessage)
    } else {
      this.setError('')
    }

    return isValid
  }

  get error() {
    return this.ringlessMessageFieldStore.error
  }
  setError = (value: string) => {
    this.ringlessMessageFieldStore.setError(value)
  }

  private _handleRenameGreetingAudio = async (attachment: Attachment) => {
    const isExist =
      typeof attachment.id === 'number'
        ? this.greetingStore.getAttachment(attachment.id)
        : undefined
    if (!isExist) {
      await this.greetingStore.loadGreetings()
      if (!isExist) {
        toastStore.add({
          type: 'error',
          title: 'Unable to rename this audio',
        })
      }
      return
    }

    new RenameModalStore({
      title: 'Rename',
      name: attachment.name || '',
      withPostfix: true,
      element: 'Greeting',
      isEditExtensionDisabled: true,
      isRenamedToast: true,
      onSave: async (name: string) => {
        await this.greetingStore.updateVoiceGreetingName(attachment, name)
      },
    })
  }

  private _defaultCategory: IResponseDefaultAudio['category'] | null = null
  private _getDefaultAudios = async () => {
    try {
      const { data } = await cacheStore.getSingletonData(CallApi.getDefaultAudio, {
        cache: true,
      })()
      return data
    } catch (e) {
      logger.error(e)
    }
    return
  }
  setDefaultCategory = (category: IResponseDefaultAudio['category']) => {
    this._defaultCategory = category
    this.ringlessMessageFieldStore.setUseDefault(this.initDefaultAudio)
  }

  initDefaultAudio = async () => {
    const category = this._defaultCategory
    if (!category) return
    const audios = await this._getDefaultAudios()
    if (audios) {
      const audio = audios.find((item) => item.category === category)
      if (audio && !this.ringlessMessageFieldStore.attachment) {
        this.ringlessMessageFieldStore.onAddAttachmentAudio(
          new Attachment({ responseDefaultAudio: audio })
        )
      }
    }
  }
}
